import React, { useCallback, useMemo } from 'react';
import { Box, Field, FieldTypes, Inscription } from '@common-fe/common-fe';
import _ from 'lodash';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { REQUIRED_TEXT } from '@/common/constants';
import { Option } from '@/common/types';
import { EmployeePhoneNumber } from '@/modules/employee/Employee/Employee.types';
import { useStore as useGeneralInfoStore } from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/stores/useGeneralInfo.store';
import theme from '@/styles/theme';
import { useFieldsWithDefaultValues } from '@/utils';

import PreferEmailPicker from '../components/PreferEmailPicker';

import useEmployeeEmailField from './useEmployeeEmailField';
import useEmployerEmailField from './useEmployerEmailField';

const EMPLOYEE_PREFER_MAIL_KEY = 'employeeEmailPrefer';
const EMPLOYER_PREFER_MAIL_KEY = 'employerEmailPrefer';

const PHONE_TYPES: Option[] = [{
  key: EmployeePhoneNumber.Work,
  value: 'Work',
}, {
  key: EmployeePhoneNumber.Home,
  value: 'Home',
}, {
  key: EmployeePhoneNumber.Mobile,
  value: 'Mobile',
}, {
  key: EmployeePhoneNumber.Unkknown,
  value: 'Unknown',
}];

const VALIDATOR = yup.string().trim().nullable();

interface Props {
  isEditMode?: boolean;
  isShowErrors?: boolean;
}

const useCommunicationChannelsFields = ({ isEditMode, isShowErrors }: Props) => {
  const defaultFields = useGeneralInfoStore((state) => state.sourceState);
  const employeeEmailPrefer = useGeneralInfoStore((state) => state.state.employeeEmailPrefer);
  const employerEmailPrefer = useGeneralInfoStore((state) => state.state.employerEmailPrefer);
  const employerEmail = useGeneralInfoStore((state) => state.state.employerEmail);
  const employeeEmail = useGeneralInfoStore((state) => state.state.employeeEmail);
  const firstPhone = useGeneralInfoStore((state) => state.state.firstPhone);
  const firstPhoneType = useGeneralInfoStore((state) => state.state.firstPhoneType);
  const secondPhone = useGeneralInfoStore((state) => state.state.secondPhone);
  const secondPhoneType = useGeneralInfoStore((state) => state.state.secondPhoneType);

  const handleSetState = useGeneralInfoStore((state) => state.setState);

  const handleChangeEmailPrefer = useCallback((key: string) => (value: boolean) => {
    const isEmployee = key === EMPLOYEE_PREFER_MAIL_KEY;
    if (isEmployee) {
      handleSetState({
        employeeEmailPrefer: value,
        employerEmailPrefer: !value,
      });
    } else {
      handleSetState({
        employeeEmailPrefer: !value,
        employerEmailPrefer: value,
      });
    }
  }, [handleSetState]);

  const employeeEmailField = useEmployeeEmailField({ isEditMode });
  const employerEmailField = useEmployerEmailField({ isEditMode });

  const emailFields = useMemo<Field[]>(() => [
    employeeEmailField as Field,
    {
      name: EMPLOYEE_PREFER_MAIL_KEY,
      type: FieldTypes.Node,
      fieldWrapperStyles: {
        marginTop: '-12px',
      },
      label: ' ',
      style: { margin: 0 },
      value: (
        <PreferEmailPicker
          disabled={!isEditMode || !employeeEmail}
          value={(!!employeeEmailPrefer && !!employeeEmail && !employerEmailPrefer)
            || (!!employeeEmail && !employerEmail)}
          onChangeValue={handleChangeEmailPrefer(EMPLOYEE_PREFER_MAIL_KEY)}
        />
      ),
    },
    employerEmailField as Field,
    {
      name: EMPLOYER_PREFER_MAIL_KEY,
      type: FieldTypes.Node,
      label: ' ',
      style: { margin: 0 },
      fieldWrapperStyles: {
        borderBottom: `1px solid ${theme.colors.border1}`,
        paddingBottom: theme.spacings.m,
        marginTop: '-12px',
        marginBottom: theme.spacings.spacing16,
      },
      value: (
        <PreferEmailPicker
          disabled={!isEditMode || !employerEmail}
          value={(!!employerEmailPrefer && !!employerEmail)
            || (!employeeEmailPrefer && !!employerEmail)
            || (!!employerEmail && !employeeEmail)}
          onChangeValue={handleChangeEmailPrefer(EMPLOYER_PREFER_MAIL_KEY)}
        />
      ),
    },
  ], [employeeEmail, employeeEmailField, employeeEmailPrefer, employerEmail,
    employerEmailField, employerEmailPrefer, handleChangeEmailPrefer, isEditMode]);

  const editableFields = useMemo<Field[]>(() => [
    ...emailFields,
    {
      name: 'firstPhoneType',
      type: FieldTypes.Dropdown,
      label: (
        <Box flex={{ grow: 1 }}>
          <Box direction="row">
            {firstPhone && !firstPhoneType && isShowErrors ? (
              <Inscription
                color="danger"
                margin={{ right: '3px', top: '-3px' }}
              >
                *
              </Inscription>
            ) : null}
            <Inscription weight="bold" color="textBody" lineHeight="20px">
              Preferred phone
            </Inscription>
          </Box>
          <Box>
            <Inscription
              weight="normal"
              color="textSecondary"
              size="small"
              lineHeight="18px"
            >
              This number used to verify your account.
            </Inscription>
          </Box>
        </Box>
      ),
      ...(firstPhone ? {
        validator: yup.string().required(REQUIRED_TEXT)
      } : {}),
      placeholder: 'Select Preferred phone type',
      singleMode: true,
      disabled: !isEditMode,
      options: PHONE_TYPES,
      ...(defaultFields?.firstPhone ? {
        defaultValue: PHONE_TYPES[0].key,
      } : {}),
      style: { marginBottom: '-4px' },
    },
    {
      name: 'firstPhone',
      type: FieldTypes.PhoneNumber,
      label: <Box />,
      disabled: !isEditMode,
      placeholder: '+1 (555) 555-1234',
      validator: VALIDATORS.PHONE_NUMBER_VALIDATE,
    },
    {
      name: 'secondPhoneType',
      type: FieldTypes.Dropdown,
      label: (
        <Box flex={{ grow: 1 }} direction="row">
          {secondPhone && !secondPhoneType && isShowErrors ? (
            <Inscription
              color="danger"
              margin={{ right: '3px', top: '-3px' }}
            >
              *
            </Inscription>
          ) : null}
          <Inscription weight="bold" color="textBody" lineHeight="20px">
            Additional phone
          </Inscription>
        </Box>
      ),
      placeholder: 'Select Additional phone type',
      options: PHONE_TYPES,
      singleMode: true,
      disabled: !isEditMode,
      ...(defaultFields?.secondPhone ? {
        defaultValue: PHONE_TYPES[1].key,
      } : {}),
      ...(secondPhone ? {
        validator: yup.string().required(REQUIRED_TEXT)
      } : { VALIDATOR }),
      style: { marginBottom: '-4px' },
    },
    {
      name: 'secondPhone',
      type: FieldTypes.PhoneNumber,
      label: <Box />,
      disabled: !isEditMode,
      placeholder: '+1 (555) 555-1234',
      validator: VALIDATORS.PHONE_NUMBER_VALIDATE.nullable(),
    },
  ], [isEditMode, defaultFields?.firstPhone, defaultFields?.secondPhone,
    firstPhone, firstPhoneType, isShowErrors, secondPhone, secondPhoneType, emailFields]);

  const viewFields = useMemo<Field[]>(() => [
    ...emailFields,
    {
      name: 'firstPhoneFormated',
      type: FieldTypes.PhoneNumber,
      label: defaultFields?.firstPhoneType ? `Preferred phone (${_.capitalize(defaultFields?.firstPhoneType)})` : 'Preferred phone',
      disabled: !isEditMode,
      placeholder: '+1 (555) 555-1234',
      value: defaultFields?.firstPhone,

    },
    {
      name: 'secondPhoneFormated',
      type: FieldTypes.PhoneNumber,
      label: defaultFields?.secondPhoneType ? `Additional phone (${_.capitalize(defaultFields?.secondPhoneType)})` : 'Additional phone',
      disabled: true,
      placeholder: '+1 (555) 555-1234',
      value: defaultFields?.secondPhone,
    },
  ], [emailFields, defaultFields?.firstPhone, defaultFields?.firstPhoneType,
    defaultFields?.secondPhone, defaultFields?.secondPhoneType, isEditMode]);
  
  const formattedFields = useFieldsWithDefaultValues(isEditMode ? editableFields : viewFields, defaultFields);

  return formattedFields;
};

export default useCommunicationChannelsFields;
